import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useDispatch, useSelector } from "react-redux";
import { track_order } from "../store/reducers/orderReducer";
import { Helmet } from "react-helmet";
import { FaSearch, FaBoxOpen, FaTruck, FaCheckCircle, FaTimesCircle, FaClock } from "react-icons/fa";
import { formatOrderDate } from "../utils/orderDate";

const STATUS_MAP = {
  pending:    { label: "Pendiente",   color: "text-yellow-600 bg-yellow-50 border-yellow-200", icon: <FaClock /> },
  processing: { label: "Procesando",  color: "text-blue-600 bg-blue-50 border-blue-200",       icon: <FaBoxOpen /> },
  warehouse:  { label: "En almacén",  color: "text-purple-600 bg-purple-50 border-purple-200", icon: <FaBoxOpen /> },
  placed:     { label: "Confirmado",  color: "text-indigo-600 bg-indigo-50 border-indigo-200", icon: <FaCheckCircle /> },
  shipped:    { label: "Enviado",     color: "text-blue-600 bg-blue-50 border-blue-200",       icon: <FaTruck /> },
  delivered:  { label: "Entregado",   color: "text-green-600 bg-green-50 border-green-200",    icon: <FaCheckCircle /> },
  cancelled:  { label: "Cancelado",   color: "text-red-600 bg-red-50 border-red-200",          icon: <FaTimesCircle /> },
};

const PAYMENT_MAP = {
  paid:   { label: "Pagado",     color: "text-green-700 bg-green-100" },
  unpaid: { label: "Pendiente",  color: "text-yellow-700 bg-yellow-100" },
};

const SeguimientoPedido = () => {
  const dispatch = useDispatch();
  const { trackedOrder, trackLoading, trackError } = useSelector(state => state.order);
  const [code, setCode] = useState("");

  const handleSearch = e => {
    e.preventDefault();
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    dispatch(track_order(trimmed));
  };

  const delivery = STATUS_MAP[trackedOrder?.delivery_status] || STATUS_MAP.pending;
  const payment  = PAYMENT_MAP[trackedOrder?.payment_status] || PAYMENT_MAP.unpaid;

  return (
    <div>
      <Helmet>
        <title>Seguimiento de Pedido | Tekos.pe</title>
      </Helmet>
      <Header />

      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">

          {/* Título */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-slate-800 mb-2">Seguimiento de Pedido</h1>
            <p className="text-gray-500">Ingresa tu código de pedido para ver el estado actual</p>
          </div>

          {/* Formulario */}
          <form onSubmit={handleSearch} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Código de pedido
            </label>
            <div className="flex gap-3">
              <input
                type="text"
                value={code}
                onChange={e => setCode(e.target.value.toUpperCase())}
                placeholder="Ej: P00000001"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg outline-none focus:border-[#ae1d22] focus:ring-2 focus:ring-[#ae1d22]/20 text-slate-700 font-mono text-lg tracking-widest"
                maxLength={9}
              />
              <button
                type="submit"
                disabled={trackLoading || !code.trim()}
                className="px-6 py-3 bg-[#ae1d22] text-white rounded-lg font-semibold hover:bg-red-800 transition-colors disabled:opacity-50 flex items-center gap-2">
                <FaSearch />
                {trackLoading ? "Buscando..." : "Buscar"}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              El código fue enviado a tu correo y WhatsApp al realizar el pedido.
            </p>
          </form>

          {/* Error */}
          {trackError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm font-medium mb-6">
              {trackError}
            </div>
          )}

          {/* Resultado */}
          {trackedOrder && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Header del pedido */}
              <div className="bg-[#ae1d22] px-6 py-4 text-white">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm opacity-80">Código de pedido</p>
                    <p className="text-2xl font-bold font-mono tracking-widest">{trackedOrder.orderCode}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm opacity-80">Total</p>
                    <p className="text-xl font-bold">S/ {trackedOrder.price?.toFixed(2)}</p>
                  </div>
                </div>
              </div>

              <div className="p-6 flex flex-col gap-5">
                {/* Estados */}
                <div className="flex gap-3 flex-wrap">
                  <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-semibold ${delivery.color}`}>
                    {delivery.icon} {delivery.label}
                  </span>
                  <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold ${payment.color}`}>
                    Pago: {payment.label}
                  </span>
                </div>

                {/* Fecha */}
                <div className="text-sm text-gray-500">
                  <span className="font-medium text-slate-700">Fecha del pedido: </span>
                  {formatOrderDate(trackedOrder)}
                </div>

                {/* Datos de envío */}
                {trackedOrder.shippingInfo && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-semibold text-slate-700 mb-2">Datos de envío</p>
                    <p className="text-sm text-gray-600">{trackedOrder.shippingInfo.name}</p>
                    <p className="text-sm text-gray-600">{trackedOrder.shippingInfo.address}</p>
                    <p className="text-sm text-gray-600">{trackedOrder.shippingInfo.city}, {trackedOrder.shippingInfo.province}</p>
                  </div>
                )}

                {/* Productos */}
                {trackedOrder.products?.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-slate-700 mb-3">Productos ({trackedOrder.products.length})</p>
                    <div className="flex flex-col gap-2">
                      {trackedOrder.products.map((p, i) => (
                        <div key={i} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                          <div>
                            <p className="text-sm font-medium text-slate-700">{p.name}</p>
                            {(p.color || p.size) && (
                              <p className="text-xs text-gray-400">{p.color}{p.color && p.size ? ' / ' : ''}{p.size}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-slate-700">S/ {p.price?.toFixed(2)}</p>
                            <p className="text-xs text-gray-400">x{p.quantity}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SeguimientoPedido;
